import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Menu, X, User, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  const { isAuthenticated, user, logout } = useAuth();
  const { getItemCount } = useCart();
  const navigate = useNavigate();

  const announcements = [
    "500+ men already upgrading",
    "The complete system — nothing held back",
    "Skin, hair, face, body, style — all included",
    "58 pages — lifetime access"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setAnnouncementIndex((prev) => (prev + 1) % announcements.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [announcements.length]);

  const itemCount = getItemCount();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {/* Announcement Bar - Gold */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-[#D4AF37] text-center py-2 px-4">
        <AnimatePresence mode="wait">
          <motion.p
            key={announcementIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="text-xs sm:text-sm text-black font-medium tracking-wide"
          >
            {announcements[announcementIndex]}
          </motion.p>
        </AnimatePresence>
      </div>
      
      <nav className="fixed top-[36px] left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Left - Menu Button */}
            <div className="flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 hover:bg-[#1a1a1a] transition-colors rounded-md"
                data-testid="menu-button"
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5 text-white" />
                ) : (
                  <Menu className="w-5 h-5 text-white" />
                )}
              </button>
            </div>

            {/* Center - Logo with animation */}
            <Link to="/" className="absolute left-1/2 transform -translate-x-1/2" data-testid="navbar-logo">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="relative group"
              >
                <span className="font-serif text-xl md:text-2xl font-semibold tracking-tight text-white relative">
                  {"The Signal.".split('').map((char, idx) => (
                    <motion.span
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        duration: 0.5, 
                        delay: 0.1 + idx * 0.05,
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                      className="inline-block"
                      style={{ 
                        marginRight: char === ' ' ? '0.25em' : '0'
                      }}
                    >
                      {char === ' ' ? '\u00A0' : char}
                    </motion.span>
                  ))}
                </span>
                {/* Subtle gold underline on hover */}
                <motion.div 
                  className="absolute -bottom-1 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent origin-center"
                  initial={{ scaleX: 0, opacity: 0 }}
                  whileHover={{ scaleX: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            </Link>

            {/* Right - Cart & User */}
            <div className="flex items-center gap-2">
              {/* Cart */}
              <Link
                to="/cart"
                className="relative p-2 hover:bg-[#1a1a1a] transition-colors rounded-md"
                data-testid="cart-button"
              >
                <ShoppingBag className="w-5 h-5 text-white" />
                {itemCount > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-white text-[11px] font-semibold rounded-full flex items-center justify-center"
                    data-testid="cart-count"
                  >
                    {itemCount}
                  </span>
                )}
              </Link>

              {/* User Menu */}
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="p-2 hover:bg-[#1a1a1a] rounded-md"
                      data-testid="user-menu-button"
                    >
                      <User className="w-5 h-5 text-white" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-[#0f0f0f] border-[#1a1a1a]">
                    <div className="px-3 py-2 text-sm text-[#999999]">
                      {user?.name || user?.email}
                    </div>
                    <DropdownMenuSeparator className="bg-[#1a1a1a]" />
                    <DropdownMenuItem asChild>
                      <Link to="/account" className="cursor-pointer text-white hover:text-[#D4AF37]" data-testid="account-link">
                        My Account
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#1a1a1a]" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="cursor-pointer text-red-500"
                      data-testid="logout-button"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="p-2 hover:bg-[#1a1a1a] rounded-md"
                    data-testid="login-button"
                  >
                    <User className="w-5 h-5 text-white" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Slide-out Menu - Smooth Apple-style */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="bg-black/95 backdrop-blur-xl border-t border-[#1a1a1a] overflow-hidden"
            >
              <motion.div 
                className="px-6 py-6 space-y-1"
                initial="closed"
                animate="open"
                variants={{
                  open: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
                  closed: { transition: { staggerChildren: 0.05, staggerDirection: -1 } }
                }}
              >
                {navLinks.map((link, idx) => (
                  <motion.div
                    key={link.path}
                    variants={{
                      open: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } },
                      closed: { opacity: 0, x: -20 }
                    }}
                  >
                    <Link
                      to={link.path}
                      className="block py-3 text-lg font-medium text-white hover:text-[#D4AF37] transition-colors active:scale-[0.98]"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`nav-link-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
                {!isAuthenticated && (
                  <motion.div
                    variants={{
                      open: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } },
                      closed: { opacity: 0, x: -20 }
                    }}
                  >
                    <Link
                      to="/login"
                      className="block py-3 text-lg font-medium text-[#D4AF37] active:scale-[0.98]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Login / Register
                    </Link>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};
